import * as React from "react";
import { css } from "@nju/css";
import { Icon } from "@nju/icons";
import { capitalizeFirstLetterOfEachWord, copyToClipboard } from "@nju/utils";
import { BlockContent } from "../BlockContent";
import { BlockText } from "../BlockText";
import { Box } from "../Box";
import { Button } from "../Pressable";
import { Separator } from "../Separator";
import { Text } from "../Text";

interface IColumn {
  key: string;
  label: string;
  value: string | JSX.Element | undefined;
  copyable?: boolean;
}

interface ICardsProps {
  columns: IColumn[];
  columnsDetails: IColumn[];
  residentialAddress?: {
    municipality?: string;
    street?: string;
    streetNumber?: string;
    apartmentNumber?: string;
  };
  addressModal: {
    isOpened: boolean;
    close: () => void;
    open: () => void;
    openButtonProps: { onPress: () => void };
    closeButtonProps: { onPress: () => void };
  };
  showAddress?: boolean;
}

export function Cards({
  columns,
  columnsDetails,
  residentialAddress,
  addressModal,
  showAddress,
}: ICardsProps) {
  const handleCopyToClipboard = React.useCallback(async (text: string) => {
    await copyToClipboard(text);
  }, []);

  const [columnFeaturesExpanded, setColumnFeaturesExpanded] =
    React.useState(false);

  return (
    <Box
      className={styles.wrapper()}
      display="flex"
      flexDirection={{
        "@onlyMobile": "column",
        "@fromTablet": "row",
      }}
    >
      <BlockContent
        spacingRow={{
          "@fromTablet": "none",
        }}
      >
        <Box
          display="flex"
          flexDirection={{
            "@onlyMobile": "column",
          }}
        >
          {columns.map((column, index) => {
            const shouldDisplayCopyButton = typeof column.value === "string";
            return (
              <Box
                className={styles.card({
                  isFirst: index === 0,
                  isLast: index === 3,
                })}
                display="flex"
                flexDirection="column"
                key={column.label}
              >
                <Text>{column.label}</Text>
                {column.value === "zweryfikowany" && (
                  <Text
                    textTransform={
                      column.key === "client" ? "capitalize" : undefined
                    }
                    tone="accent"
                    weight="bold"
                  >
                    {column.value}
                  </Text>
                )}
                {column.value !== "zweryfikowany" && (
                  <Text breakLongValue="force" weight="bold">
                    {column.value}
                    {column.copyable && shouldDisplayCopyButton && (
                      <Button
                        display="inline"
                        onPress={() => {
                          handleCopyToClipboard(column.value as string);
                        }}
                        size="inline"
                        variant="simple"
                      >
                        <Icon className={styles.copyIcon()} name="copy" />
                      </Button>
                    )}
                  </Text>
                )}
                <div className="hidden only-mobile:block">
                  <Separator size="s" />
                </div>
              </Box>
            );
          })}
        </Box>
        {columnFeaturesExpanded && (
          <>
            <Separator
              size={{
                "@onlyMobile": "none",
                "@fromTablet": "s",
              }}
              tone="invisible"
            />
            <Box
              display="flex"
              flexDirection={{
                "@onlyMobile": "column",
              }}
            >
              {columnsDetails.map((columnDetails, index) => {
                return (
                  <Box
                    className={styles.card({
                      isFirst: index === 0,
                      isLast: index === 3,
                    })}
                    display="flex"
                    flexDirection="column"
                    key={columnDetails.label}
                  >
                    <Text>{columnDetails.label}</Text>
                    <Text weight="bold">{columnDetails.value}</Text>
                    <div className="hidden only-mobile:block">
                      <Separator size="s" />
                    </div>
                  </Box>
                );
              })}
            </Box>
            {showAddress && (
              <>
                <Separator
                  size={{
                    "@onlyMobile": "none",
                    "@fromTablet": "s",
                  }}
                  tone="invisible"
                />
                <div className="from-tablet:border-r-1 border-geyser max-w-[250px] w-full pr-3">
                  <div className="flex justify-between items-center">
                    <Text>adres zamieszkania</Text>
                    <Button
                      onPress={addressModal.open}
                      size="inline"
                      variant="simple"
                      withUnderline={true}
                    >
                      {residentialAddress ? "zmień" : "dodaj"}
                    </Button>
                  </div>
                  {residentialAddress ? (
                    <>
                      <BlockText size="m" weight="bold">
                        {residentialAddress?.municipality &&
                          capitalizeFirstLetterOfEachWord(
                            residentialAddress?.municipality,
                          )}
                        ,
                      </BlockText>
                      <BlockText size="m" weight="bold">
                        {residentialAddress?.street
                          ? `${capitalizeFirstLetterOfEachWord(
                              residentialAddress?.street,
                            )} `
                          : ""}
                        {residentialAddress?.streetNumber &&
                          capitalizeFirstLetterOfEachWord(
                            residentialAddress?.streetNumber,
                          )}
                        {residentialAddress?.apartmentNumber
                          ? `/${residentialAddress?.apartmentNumber}`
                          : ""}
                      </BlockText>
                    </>
                  ) : (
                    "-"
                  )}
                </div>
              </>
            )}
          </>
        )}
      </BlockContent>
      <div>
        <button
          className={styles.trigger()}
          onClick={() => {
            return setColumnFeaturesExpanded((columnFeaturesExpanded) => {
              return !columnFeaturesExpanded;
            });
          }}
        >
          {columnFeaturesExpanded ? "ukryj" : "pokaż"} szczegóły
          <Icon
            className={styles.triggerIcon()}
            name={columnFeaturesExpanded ? "chevron-up" : "chevron-down"}
            tone="neutral"
          />
        </button>
      </div>
    </Box>
  );
}

const styles = {
  wrapper: css({
    backgroundColor: "$white",
    py: "$15",
    "@onlyMobile": {
      px: "$10",
    },
    maxWidth: "$maxGridWidth",
    mx: "$auto",
  }),
  copyIcon: css({
    ml: "$10",
    mb: "$10",
  }),
  card: css({
    flex: "1",
    "@fromTablet": {
      px: "$15",
      maxWidth: "250px",
    },
    width: "100%",
    variants: {
      isFirst: {
        true: {
          "@fromTablet": {
            borderWidth: "0px",
            paddingLeft: 0,
          },
        },
        false: {
          "@fromTablet": {
            borderLeft: "1px solid $geyser",
          },
        },
      },
      isLast: {
        true: {
          "@fromTablet": {
            maxWidth: "280px",
          },
        },
      },
    },
  }),
  trigger: css({
    backgroundColor: "transparent",
    border: "none",
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    minWidth: "140px",
    "@fromTablet": {
      ml: "30px",
      pt: "$10",
    },
    fontSize: "$14",
    fontWeight: "bold",
  }),
  triggerIcon: css({
    ml: "$12",
    flexShrink: 0,
    width: "16px",
    height: "16px",
    "@fromTablet": {
      ml: "$5",
    },
    "@fromDesktop": {
      ml: "$12",
    },
  }),
};
